<template>
  <div class="pa-0 secondary--text text--darken-3">
    <!-- Header Area -->
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="d-flex align-center mb-2">
        <v-avatar color="primary" size="40" class="mr-4 shadow-sm">
          <v-icon color="white" small>fas fa-warehouse</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">{{ item.id > -1 ? 'Editar Bodega' : 'Nueva Bodega' }}</div>
          <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Configuración de puntos de almacenamiento</div>
        </div>
      </div>
    </div>

    <div class="pa-8">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">
          
          <!-- Section: Basic Info -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-info-circle</v-icon> Datos Generales
          </div>

          <v-row dense class="mb-6">
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="item.name"
                  label="Nombre de la Bodega"
                  placeholder="Ej: Bodega Central de Materias Primas"
                  outlined
                  dense
                  class="modern-input bg-white"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-tag"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Section: Advanced Config -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-cog</v-icon> Comportamiento de Inventario
          </div>

          <v-card outlined class="modern-selection-card pa-5 mb-8 transition-fast" :class="item.add_stock ? 'active-selection' : ''">
            <div class="d-flex align-center">
              <v-switch
                v-model="item.add_stock"
                hide-details
                dense
                class="mt-0 pt-0 modern-switch mr-4"
                color="primary"
              ></v-switch>
              <div>
                <div class="body-2 font-weight-black" :class="item.add_stock ? 'primary--text' : 'grey--text text--darken-3'">¿Sumar al Inventario Global?</div>
                <div class="caption grey--text lh-1-2 mt-1">Si está activo, las existencias en esta bodega contarán para el stock total disponible en el sistema.</div>
              </div>
            </div>
          </v-card>

          <!-- Footer Buttons -->
          <div class="d-flex mt-10 gap-3">
            <v-btn 
              type="submit" 
              color="primary" 
              large 
              depressed 
              :loading="loading"
              class="flex-grow-1 py-6 rounded-lg font-weight-black text-uppercase tracking-widest shadow-md"
            >
              <v-icon small class="mr-2">fas fa-save</v-icon>
              {{ item.id > -1 ? 'Guardar Cambios' : 'Crear Bodega' }}
            </v-btn>
            <v-btn 
              text 
              large 
              color="grey darken-1"
              class="rounded-lg font-weight-bold px-8"
              @click.prevent="$emit('close')"
            >
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { getStore, createStore, updateStore } from '@/api/stocks'
import notifications from '@/utils/notifications'

export default {
  name: 'StoreCreateUpdate',
  props: {
    id: { required: true }
  },
  data() {
    return {
      item: { 
        id: -1,
        name: '', 
        add_stock: true 
      },
      loading: false,
    }
  },
  methods: {
    async save() {
      this.loading = true
      const payload = {
        name: this.item.name,
        add_stock: this.item.add_stock
      }
      try {
        if (this.id > -1) {
          await updateStore(this.id, payload)
          notifications.showSuccess('Bodega actualizada')
        } else {
          await createStore(payload)
          notifications.showSuccess('Nueva bodega registrada')
        }
        await this.$store.dispatch('getStores')
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error al guardar: ' + err.message)
      } finally {
        this.loading = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getStore(this.id)
          this.item = res
        } catch (err) {
          notifications.showError('Error al cargar datos')
        } finally {
          this.loading = false
        }
      } else {
        this.item = { id: -1, name: '', add_stock: true }
      }
    }
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8f9fa; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.gap-3 { gap: 12px; }

.modern-input >>> .v-input__control { min-height: 48px !important; }
.modern-input >>> fieldset { border: 1px solid rgba(0,0,0,0.1) !important; border-radius: 8px !important; }
.modern-input >>> .v-input__prepend-inner { margin-top: 12px !important; margin-right: 12px !important; }

.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }

.modern-selection-card { border-radius: 12px !important; border: 1.5px solid rgba(0,0,0,0.05) !important; background-color: #fff; }
.active-selection { border-color: var(--v-primary-base) !important; background-color: rgba(var(--v-primary-base), 0.02) !important; }
.transition-fast { transition: all 0.2s ease; }
</style>